import { formatMoney } from "@szerviz/core";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApp } from "../context";
import { PAYMENT_METHODS, type CancelledInvoice, type Customer, type Invoice, type PaymentMethod, type WorkOrder } from "../data";
import { missingBuyerData } from "../../../../supabase/functions/_shared/szamlazz";
import { ErrorText } from "./ErrorText";
import { TextInput } from "./Field";

/** Before closing: how the customer pays (visible cards, one must be chosen). */
export function PaymentChoice({ value, onChange }: { value: PaymentMethod | null; onChange: (m: PaymentMethod) => void }) {
  const { t } = useTranslation();
  return (
    <fieldset className="choice-cards">
      <legend>{t("payment.title")}</legend>
      {PAYMENT_METHODS.map((m) => (
        <label key={m} className={`choice-card${value === m ? " selected" : ""}`}>
          <input type="radio" name="paymentMethod" checked={value === m} onChange={() => onChange(m)} />
          <span><strong>{t(`payment.${m}`)}</strong></span>
        </label>
      ))}
    </fieldset>
  );
}

/**
 * On a closed work order: the payment (a transfer is marked when it arrives) and the
 * invoice (issued once through Számlázz.hu; for a work order closed without one it can be
 * issued later, after choosing the payment method). The owner or a manager can cancel an issued
 * invoice with a cancelling invoice (sztornó, with a reason); then the work order can be reopened
 * and a new invoice issued.
 */
export function PaymentInvoice({ order, customer, onChanged, onInvoice }: {
  order: WorkOrder;
  customer: Customer | null;
  onChanged: () => Promise<void>;
  /** Tells the work order whether an invoice exists (it cannot be reopened then). */
  onInvoice?: (invoice: Invoice | null) => void;
}) {
  const { t, i18n } = useTranslation();
  const { store, workshop, role } = useApp();
  const [number, setNumber] = useState("");
  const [invoice, setInvoice] = useState<Invoice | null>(null);
  const [cancelled, setCancelled] = useState<CancelledInvoice[]>([]);
  const [cancelling, setCancelling] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelNumber, setCancelNumber] = useState("");
  const [keySet, setKeySet] = useState(false);
  const [method, setMethod] = useState<PaymentMethod | null>(order.paymentMethod ?? null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    const inv = await store.invoiceOf(order.id);
    setInvoice(inv);
    onInvoice?.(inv);
    setCancelled(await store.cancelledInvoices(order.id));
    setKeySet(await store.invoiceKeySet(workshop.id));
  }, [order.id, store, workshop.id, onInvoice]);
  useEffect(() => void load(), [load]);

  const run = async (fn: () => Promise<unknown>) => {
    setError(null);
    setBusy(true);
    try {
      await fn();
      await onChanged();
      await load();
    } catch (err) {
      setError(err);
      // an unknown outcome (invoice or cancellation) is kept: show it at once
      await load().catch(() => undefined);
    } finally {
      setBusy(false);
    }
  };
  const manager = role === "owner" || role === "manager";
  const cancel = () =>
    run(async () => {
      await store.cancelInvoice(order.id, cancelReason);
      setCancelling(false);
      setCancelReason("");
    });
  const missing = missingBuyerData({ name: customer?.name, postalCode: customer?.postalCode, city: customer?.city, address: customer?.addressLine });
  const fieldName = (k: string) => t(k === "name" ? "customer.name" : `customer.${k}`);
  // an invoice later for a work order closed without one: the payment method is asked first
  const issue = () =>
    run(async () => {
      if (method && method !== order.paymentMethod) await store.markWorkOrderPaid(order.id, method, method !== "transfer");
      await store.issueInvoice(order.id);
    });

  return (
    <div className="payment-invoice">
      <ErrorText error={error} />
      {order.paymentMethod && (
        <p className={order.paidAt ? undefined : "warn-text"}>
          {t(`payment.${order.paymentMethod}`)} · {order.paidAt ? t("payment.paidOn", { date: new Date(order.paidAt).toLocaleDateString(i18n.language) }) : t("payment.unpaid")}
          {!order.paidAt && (
            <>
              {" "}
              <button type="button" disabled={busy} onClick={() => void run(() => store.markWorkOrderPaid(order.id, null, true))}>
                {t("payment.markPaid")}
              </button>
            </>
          )}
        </p>
      )}
      {invoice && invoice.status !== "issued" ? (
        <div className="invoice-unknown">
          <p className="warn-text">{invoice.status === "pending" ? t("invoice.pending") : t("invoice.unknown")}</p>
          {invoice.status === "unknown" && (role === "owner" || role === "manager") && (
            <div className="actions">
              <label className="field">
                <span>{t("invoice.unknownNumber")}</span>
                <input id="invoice-number" value={number} onChange={(e) => setNumber(e.target.value)} />
              </label>
              <button type="button" className="primary" disabled={busy || !number.trim()} onClick={() => void run(() => store.resolveInvoice(order.id, number))}>
                {t("invoice.resolveIssued")}
              </button>
              <button type="button" disabled={busy} onClick={() => void run(() => store.resolveInvoice(order.id, null))}>
                {t("invoice.resolveNotIssued")}
              </button>
            </div>
          )}
        </div>
      ) : invoice ? (
        <>
          <p>
            {t("invoice.issued", { number: invoice.number ?? "", gross: invoice.gross === null ? "–" : formatMoney(invoice.gross, invoice.currency, i18n.language) })}{" "}
            {invoice.viewUrl && (
              <a href={invoice.viewUrl} target="_blank" rel="noopener noreferrer">{t("invoice.open")}</a>
            )}
          </p>
          {invoice.cancelStatus ? (
            <div className="invoice-unknown">
              <p className="warn-text">{invoice.cancelStatus === "pending" ? t("invoice.cancelPending") : t("invoice.cancelUnknown")}</p>
              {invoice.cancelStatus === "unknown" && manager && (
                <div className="actions">
                  <label className="field">
                    <span>{t("invoice.cancelUnknownNumber")}</span>
                    <input id="invoice-cancel-number" value={cancelNumber} onChange={(e) => setCancelNumber(e.target.value)} />
                  </label>
                  <button type="button" className="primary" disabled={busy || !cancelNumber.trim()} onClick={() => void run(async () => { await store.resolveInvoiceCancel(order.id, cancelNumber); setCancelNumber(""); setCancelling(false); })}>
                    {t("invoice.resolveCancelled")}
                  </button>
                  <button type="button" disabled={busy} onClick={() => void run(async () => { await store.resolveInvoiceCancel(order.id, null); setCancelling(false); setCancelReason(""); })}>
                    {t("invoice.resolveNotCancelled")}
                  </button>
                </div>
              )}
            </div>
          ) : (
            manager && keySet && (cancelling ? (
              <div className="invoice-cancel">
                <p className="muted">{t("invoice.cancelText")}</p>
                <TextInput label={t("invoice.cancelReason")} value={cancelReason} onChange={setCancelReason} help="invoice.cancel" required />
                <div className="actions">
                  <button type="button" className="danger" disabled={busy || !cancelReason.trim()} onClick={() => void cancel()}>
                    {busy ? t("invoice.cancelling") : t("invoice.cancelConfirm")}
                  </button>
                  <button type="button" disabled={busy} onClick={() => { setCancelling(false); setCancelReason(""); }}>{t("common.cancel")}</button>
                  {!cancelReason.trim() && <span className="missing">{t("invoice.cancelReasonMissing")}</span>}
                </div>
              </div>
            ) : (
              <div className="actions">
                <button type="button" onClick={() => setCancelling(true)}>{t("invoice.cancel")}</button>
              </div>
            ))
          )}
        </>
      ) : (
        keySet && (
          <div className="invoice-later">
            {!order.paymentMethod && <PaymentChoice value={method} onChange={setMethod} />}
            <div className="actions">
              <button type="button" className="primary" disabled={busy || missing.length > 0 || !method} onClick={() => void issue()}>
                {busy ? t("invoice.issuing") : t("invoice.issue")}
              </button>
              {missing.length > 0 && <span className="missing">{t("invoice.customerMissing", { fields: missing.map(fieldName).join(", ") })}</span>}
              {missing.length === 0 && !method && <span className="missing">{t("payment.choose")}</span>}
            </div>
          </div>
        )
      )}
      {cancelled.map((c, i) => (
        <p key={`${c.cancelNumber}-${i}`} className="invoice-cancelled">
          {t("invoice.cancelled", { number: c.cancelNumber ?? "", original: c.number ?? "" })}
          {c.cancelReason ? ` · ${t("invoice.cancelledReason", { reason: c.cancelReason })}` : ""}
        </p>
      ))}
    </div>
  );
}

/** Settings (owner): the Számlázz.hu Agent key; only whether it is set is ever shown. */
export function InvoicingSettings({ isOwner }: { isOwner: boolean }) {
  const { t } = useTranslation();
  const { store, workshop } = useApp();
  const [set, setSet] = useState(false);
  const [key, setKey] = useState("");
  const [error, setError] = useState<unknown>(null);
  const load = useCallback(async () => setSet(await store.invoiceKeySet(workshop.id)), [store, workshop.id]);
  useEffect(() => void load(), [load]);
  const save = async (value: string) => {
    setError(null);
    try {
      await store.setInvoiceKey(workshop.id, value);
      setKey("");
      await load();
    } catch (err) {
      setError(err);
    }
  };
  return (
    <section aria-label={t("invoice.settings")}>
      <h2>{t("invoice.settings")}</h2>
      <p className={set ? "ok" : "warn-text"}>{set ? t("invoice.keySet") : t("invoice.keyNotSet")}</p>
      <p className="muted">{t("invoice.keyHelp")}</p>
      <ErrorText error={error} />
      {isOwner && (
        <div className="form-grid">
          <label className="field wide">
            <span>{t("invoice.key")}</span>
            <input id="szamlazz-key" type="password" autoComplete="off" value={key} onChange={(e) => setKey(e.target.value)} />
          </label>
          <div className="actions wide">
            <button type="button" className="primary" disabled={!key.trim()} onClick={() => void save(key)}>{t("common.save")}</button>
            {set && <button type="button" className="danger" onClick={() => void save("")}>{t("invoice.keyClear")}</button>}
          </div>
        </div>
      )}
    </section>
  );
}
